/**
 * CE of a mock or a practice in free navigation (state.navigable), as on the centre's computer:
 * a grid of the 39 numbers, go back and forth, change an answer until the time is up. Each choice
 * is saved at once on the server; « Terminer la section » closes it. CO stays sequential.
 */

import { ApiError, academyAttemptsAnswerCreate, academyAttemptsNextCreate } from "@agencyos/api-client";
import { firstUnanswered, hasPictures } from "@agencyos/core";
import { Button } from "@agencyos/ui-web";
import { Check, ChevronLeft, ChevronRight, CheckCircle2, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ExamBar, ExamNav, ExamPill, FinishDialog, focusQuestion, useAnswerKeys } from "./ExamFrame";
import { ExamPictureView, PictureOptions } from "./ExamPicture";
import { SectionDone } from "./McqSection";
import { type NavQuestion, type PlayerState, isObject, num, str } from "./types";

interface Correction {
  correct: boolean;
  answer: number;
  explanation: string;
}

const LETTERS = ["A", "B", "C", "D", "E", "F"];

export function CeNavigator({
  attemptId,
  state,
  training,
  onChanged,
}: {
  attemptId: string;
  state: PlayerState;
  training: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const questions: NavQuestion[] = state.questions;
  // Local choices win over the server copy while a save is on its way (no flicker).
  const [choices, setChoices] = useState<Record<string, number>>({});
  const [current, setCurrent] = useState(() => firstUnanswered(questions.map((q) => q.choice)));
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [corrections, setCorrections] = useState<Record<string, Correction>>({});
  const [confirm, setConfirm] = useState(false);
  const [closing, setClosing] = useState(false);
  const stem = useRef<HTMLHeadingElement>(null);
  const moved = useRef(false);

  useEffect(() => {
    if (moved.current) focusQuestion(stem.current);
    moved.current = true;
  }, [current]);

  const choiceOf = (q: NavQuestion): number | null => choices[q.id] ?? q.choice;
  const total = questions.length;
  const answered = questions.filter((q) => choiceOf(q) !== null).length;
  const index = Math.min(Math.max(current, 0), Math.max(total - 1, 0));
  const question = questions[index];

  const choose = async (q: NavQuestion, choice: number) => {
    const before = choices[q.id];
    setChoices((prev) => ({ ...prev, [q.id]: choice }));
    setSaving(q.id);
    setError(null);
    try {
      const response = await academyAttemptsAnswerCreate(attemptId, { question: q.id, choice });
      const data: unknown = response.data;
      if (training && isObject(data) && typeof data.correct === "boolean") {
        setCorrections((prev) => ({
          ...prev,
          [q.id]: { correct: data.correct === true, answer: num(data.answer, -1), explanation: str(data.explanation) },
        }));
      }
    } catch (failure) {
      setChoices((prev) =>
        before === undefined
          ? Object.fromEntries(Object.entries(prev).filter(([key]) => key !== q.id))
          : { ...prev, [q.id]: before },
      );
      // Time up (the section is closed on the server): the new state decides what comes next.
      if (failure instanceof ApiError && failure.status === 400) await onChanged();
      else setError(t("academy.player.networkError"));
    } finally {
      setSaving(null);
    }
  };

  // A / B / C / D answer the question on screen, Enter goes to the next one (never closes the section).
  const onScreen = questions[Math.min(Math.max(current, 0), Math.max(questions.length - 1, 0))];
  useAnswerKeys(
    onScreen?.options.length ?? 0,
    (i) => {
      if (onScreen && !corrections[onScreen.id]) void choose(onScreen, i);
    },
    () => {
      if (current < questions.length - 1) setCurrent(Math.max(current, 0) + 1);
    },
  );

  const finish = async () => {
    setClosing(true);
    try {
      await academyAttemptsNextCreate(attemptId);
    } catch {
      // Already closed (time up in another tab): the refreshed state shows the next section.
    } finally {
      await onChanged();
      setClosing(false);
    }
  };

  if (!question) {
    return <SectionDone onContinue={() => void finish()} busy={closing} />;
  }

  const chosen = choiceOf(question);
  const correction = corrections[question.id] ?? null;
  const unanswered = total - answered;

  return (
    <div className="ac-ce">
      <section className="ac-question ac-xq" aria-labelledby={`nq-${question.id}`}>
        <div className="ac-question__sheet">
          {question.document && (
            <div key={`doc-${question.id}`} className="ac-document" lang="fr">
              {question.document}
            </div>
          )}
          <ExamPictureView key={question.id} picture={question.image} lang="fr" />
          <h3 id={`nq-${question.id}`} ref={stem} tabIndex={-1} className="ac-stem" lang="fr">
            {question.stem}
          </h3>
          <fieldset className="plain-fieldset ac-options-set" disabled={correction !== null}>
            <legend className="visually-hidden">{t("academy.player.options")}</legend>
            {hasPictures(question.option_images) ? (
              <PictureOptions
                key={question.id}
                name={`nq-${question.id}`}
                options={question.options}
                images={question.option_images}
                value={chosen}
                onChange={(i) => void choose(question, i)}
                answer={correction?.answer ?? null}
                given={correction ? chosen : null}
                lang="fr"
                legend={t("academy.player.options")}
              />
            ) : (
            <div className={`ac-options${question.options.length === 4 ? " ac-options--quad" : ""}`} lang="fr">
              {question.options.map((option, i) => {
                const mark =
                  correction === null
                    ? ""
                    : i === correction.answer
                      ? " ac-option--right"
                      : i === chosen
                        ? " ac-option--wrong"
                        : "";
                return (
                  <label key={i} className={`ac-option${chosen === i ? " ac-option--on" : ""}${mark}`}>
                    <input
                      type="radio"
                      name={`nq-${question.id}`}
                      checked={chosen === i}
                      onChange={() => void choose(question, i)}
                    />
                    <span className="ac-option__letter" aria-hidden>
                      {LETTERS[i]}
                    </span>
                    <span className="ac-option__text">{option}</span>
                    {chosen === i && correction === null && <Check aria-hidden size={18} className="ac-option__check" />}
                  </label>
                );
              })}
            </div>
            )}
          </fieldset>
          {correction && (
            <div className={`ac-correction${correction.correct ? " ac-correction--ok" : ""}`} role="status">
              <p className="ac-status">
                {correction.correct ? <CheckCircle2 aria-hidden size={18} /> : <XCircle aria-hidden size={18} />}
                <strong>
                  {correction.correct
                    ? t("academy.player.right")
                    : t("academy.player.wrong", { letter: LETTERS[correction.answer] ?? "" })}
                </strong>
              </p>
              {correction.explanation && <p>{correction.explanation}</p>}
            </div>
          )}
          {error && (
            <p className="ko" role="alert">
              {error}
            </p>
          )}
        </div>
        <ExamBar
          hint={
            <>
              <span className="ac-save-state" aria-live="polite">
                {saving === question.id ? (
                  <>
                    <span className="ac-dot ac-dot--busy" aria-hidden="true" />
                    {t("academy.navigator.saving")}
                  </>
                ) : chosen !== null ? (
                  <>
                    <span className="ac-dot ac-dot--ok" aria-hidden="true" />
                    {t("academy.navigator.saved")}
                  </>
                ) : (
                  ""
                )}
              </span>
              <span className="ac-xbar__keys">{t("examFrame.keysNext")}</span>
            </>
          }
          left={
            <Button
              variant="secondary"
              icon={<ChevronLeft aria-hidden size={18} />}
              disabled={index === 0}
              onClick={() => setCurrent(index - 1)}
            >
              {t("academy.navigator.previous")}
            </Button>
          }
          pills={
            <>
              <ExamPill>{t("academy.player.progress", { n: index + 1, total })}</ExamPill>
              {/* navigable: a mock or a practice, never the adaptive placement */}
              {question.level && <ExamPill>{question.level}</ExamPill>}
            </>
          }
          right={
            index < total - 1 ? (
              <Button onClick={() => setCurrent(index + 1)}>
                {t("academy.navigator.next")}
                <ChevronRight aria-hidden size={18} />
              </Button>
            ) : (
              <Button onClick={() => setConfirm(true)}>{t("academy.navigator.finish")}</Button>
            )
          }
        />
      </section>

      <ExamNav
        cells={questions.map((q, i) => ({
          key: q.id,
          text: String(i + 1),
          state: choiceOf(q) !== null ? "done" : "empty",
          current: i === index,
          onSelect: () => setCurrent(i),
        }))}
      >
        <p className="ac-footnote">{t("academy.navigator.hint")}</p>
        <Button variant="secondary" block onClick={() => setConfirm(true)}>
          {t("academy.navigator.finish")}
        </Button>
      </ExamNav>
      <FinishDialog open={confirm} unanswered={unanswered} busy={closing || saving !== null} onCancel={() => setConfirm(false)} onConfirm={() => void finish()} />
    </div>
  );
}
